import React, { useState } from 'react'
import { Form } from '@inertiajs/react'
import { Eye, EyeOff } from 'lucide-react'
import { AuthLayout } from '@/layouts/AuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Props = {
  invitation_token: string
  first_name: string
  last_name: string
}

type StrengthLevel = 'weak' | 'medium' | 'strong'

function calculateStrength(password: string): StrengthLevel {
  if (password.length === 0) return 'weak'
  let score = 0
  if (password.length >= 6) score++
  if (password.length >= 10) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++
  if (score <= 1) return 'weak'
  if (score <= 3) return 'medium'
  return 'strong'
}

const strengthConfig: Record<StrengthLevel, { label: string; color: string; width: string }> = {
  weak:   { label: 'Weak',   color: 'bg-red-500',    width: 'w-1/3' },
  medium: { label: 'Medium', color: 'bg-yellow-500', width: 'w-2/3' },
  strong: { label: 'Strong', color: 'bg-green-500',  width: 'w-full' },
}

function PasswordStrengthBar({ password }: { password: string }) {
  if (password.length === 0) return null
  const level = calculateStrength(password)
  const { label, color, width } = strengthConfig[level]

  return (
    <div className="mt-1.5 space-y-1">
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${color} ${width}`}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Password strength: <span className="font-medium">{label}</span>
      </p>
    </div>
  )
}

function InvitationAcceptPage({ invitation_token, first_name, last_name }: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [passwordValue, setPasswordValue] = useState('')

  return (
    <Form method="put" action="/users/invitation">
      {({ errors, processing }: { errors: Record<string, string[]>; processing: boolean }) => (
        <div className="space-y-4">
          {/* Hidden token */}
          <input
            type="hidden"
            name="user[invitation_token]"
            value={invitation_token}
          />

          {/* First name field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_first_name">First name</Label>
            <Input
              id="user_first_name"
              name="user[first_name]"
              type="text"
              autoComplete="given-name"
              placeholder="First name"
              defaultValue={first_name}
              aria-invalid={!!errors['user[first_name]']}
            />
            {errors['user[first_name]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[first_name]'][0]}</p>
            )}
          </div>

          {/* Last name field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_last_name">Last name</Label>
            <Input
              id="user_last_name"
              name="user[last_name]"
              type="text"
              autoComplete="family-name"
              placeholder="Last name"
              defaultValue={last_name}
              aria-invalid={!!errors['user[last_name]']}
            />
            {errors['user[last_name]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[last_name]'][0]}</p>
            )}
          </div>

          {/* Password field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_password">Password</Label>
            <div className="relative">
              <Input
                id="user_password"
                name="user[password]"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={!!errors['user[password]']}
                onChange={(e) => setPasswordValue(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <PasswordStrengthBar password={passwordValue} />
            {errors['user[password]'] && (
              <p className="text-sm text-destructive mt-1">{errors['user[password]'][0]}</p>
            )}
          </div>

          {/* Password confirmation field */}
          <div className="space-y-1.5">
            <Label htmlFor="user_password_confirmation">Confirm password</Label>
            <div className="relative">
              <Input
                id="user_password_confirmation"
                name="user[password_confirmation]"
                type={showConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={!!errors['user[password_confirmation]']}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirmation((prev) => !prev)}
                aria-label={showConfirmation ? 'Hide password' : 'Show password'}
              >
                {showConfirmation ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors['user[password_confirmation]'] && (
              <p className="text-sm text-destructive mt-1">
                {errors['user[password_confirmation]'][0]}
              </p>
            )}
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={processing}>
            {processing ? 'Activating...' : 'Activate my account'}
          </Button>
        </div>
      )}
    </Form>
  )
}

InvitationAcceptPage.layout = (page: React.ReactNode) => (
  <AuthLayout
    title="Welcome to SOS Tunisie"
    description="Complete your profile and set your password to activate your account"
  >
    {page}
  </AuthLayout>
)

export default InvitationAcceptPage
